import React from 'react';
import { 
  IoCodeWorking, 
  IoPhonePortrait, 
  IoSearch, 
  IoShieldCheckmark, 
  IoCloudUpload,
  IoSchool,
  IoAnalytics,
  IoServer
} from 'react-icons/io5';
import AnimatedSection from '../ui/AnimatedSection';
import AnimatedCard from '../ui/AnimatedCard';

const Services = () => {
  const services = [
    {
      icon: <IoCodeWorking className="text-5xl" />,
      title: "Custom Web Development",
      description: "Build scalable, high-performance web applications that engage users and drive business growth. We deliver responsive solutions using modern frameworks.",
      features: ["React, Angular, Vue.js", "Node.js, ASP.NET", "RESTful APIs", "Cloud Integration"]
    },
    {
      icon: <IoPhonePortrait className="text-5xl" />,
      title: "Mobile App Development",
      description: "Create native and cross-platform mobile applications that provide seamless user experiences across iOS and Android devices.",
      features: ["Native iOS & Android", "React Native", "Flutter", "App Store Optimization"]
    },
    {
      icon: <IoSearch className="text-5xl" />,
      title: "Website Development & SEO",
      description: "Design professional websites that convert visitors into customers. Our SEO-optimized solutions improve search rankings and generate qualified leads.",
      features: ["Responsive Design", "SEO Optimization", "Performance Tuning", "Content Management"]
    },
    {
      icon: <IoShieldCheckmark className="text-5xl" />,
      title: "Software Testing & QA",
      description: "Ensure flawless performance with comprehensive testing services. Reduce bugs, improve user experience, and launch products with confidence.",
      features: ["Manual Testing", "Automated Testing", "Performance Testing", "Security Testing"]
    },
    {
      icon: <IoCloudUpload className="text-5xl" />,
      title: "Cloud Solutions & DevOps",
      description: "Leverage cloud infrastructure to scale your business. We provide cloud migration, deployment, and DevOps services for seamless operations.",
      features: ["AWS, Azure, GCP", "CI/CD Pipelines", "Containerization", "Infrastructure as Code"]
    },
    {
      icon: <IoSchool className="text-5xl" />,
      title: "IT Training & Consulting",
      description: "Build your team's capabilities with hands-on training from industry experts. Equip your staff with practical skills that deliver immediate value.",
      features: ["Corporate Training", "Certification Programs", "Technology Consulting", "Best Practices"]
    },
    {
      icon: <IoAnalytics className="text-5xl" />,
      title: "Digital Transformation",
      description: "Transform your business processes with strategic technology solutions. Streamline operations and enhance customer engagement.",
      features: ["Process Automation", "Data Analytics", "Business Intelligence", "System Integration"]
    },
    {
      icon: <IoServer className="text-5xl" />,
      title: "Database & Backend Services",
      description: "Design and optimize robust database solutions. Ensure data integrity, performance, and scalability for your applications.",
      features: ["SQL Server, MySQL", "MongoDB, PostgreSQL", "Database Optimization", "Data Migration"]
    }
  ];

  return (
    <section id="services" className="py-20 dark-section relative">
      <div className="container mx-auto px-4 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-8">
            <h2 className="text-sm font-semibold text-blue-400 uppercase tracking-wider mb-2">Services</h2>
            <p className="text-3xl md:text-4xl font-bold text-white">Our Comprehensive IT Solutions</p>
          </div>
        </AnimatedSection>
        <AnimatedSection delay={0.2}>
          <p className="text-center text-gray-300 max-w-3xl mx-auto mb-12">
            We deliver end-to-end IT services that help businesses reduce costs, improve efficiency, and accelerate growth. 
            From development to deployment, we've got you covered.
          </p>
        </AnimatedSection>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
          {services.map((service, index) => (
            <AnimatedCard key={index} index={index}>
              <ServiceCard {...service} />
            </AnimatedCard>
          ))}
        </div>
      </div>
    </section>
  );
};

// Service Card Component
const ServiceCard = ({ icon, title, description, features }) => {
  return (
    <div className="group dark-card rounded-xl transition-all duration-300 p-6 h-full flex flex-col relative z-10">
      <div className="mb-4 text-blue-400 group-hover:text-blue-300 transition-colors transform group-hover:scale-110 duration-300">
        {icon}
      </div>
      <h4 className="text-xl font-bold mb-3 text-white group-hover:text-blue-400 transition-colors">{title}</h4>
      <p className="text-gray-300 text-sm mb-4 flex-grow">{description}</p>
      <ul className="space-y-2">
        {features.map((feature, idx) => (
          <li key={idx} className="flex items-start text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
            <span className="text-blue-400 mr-2">✓</span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Services;